import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import fulltimeJSON from "../data/fulltime.json";
import internshipsJSON from "../data/internships.json";
import { default as EducationEnd } from "./EducationEnd";
import { default as EducationStart } from "./EducationStart";
import GlassCard from "./GlassCard";
import Job from "./Job";

export default function About(props) {
  const fulltime = fulltimeJSON.FullTime;
  const internships = internshipsJSON.Internships;
  const isMobile = useMediaQuery("(max-width: 700px)");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Box>
        <GlassCard
          sx={{
            position: "relative",
            overflow: "hidden",
            padding: isMobile ? "28px 22px" : "44px 40px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "3px",
              background:
                "linear-gradient(90deg, #FF83A8 0%, #b39ddb 50%, #85e0ff 100%)",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 22,
                background: "#FF83A8",
                borderRadius: "2px",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="overline"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                letterSpacing: "2.5px",
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.68rem",
                lineHeight: 1,
              }}
            >
              ABOUT ME
            </Typography>
          </Box>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            color="primary"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 700,
              mb: 0.75,
              letterSpacing: "-0.5px",
              lineHeight: 1.15,
              textAlign: "center",
            }}
          >
            Hi, I'm Patricia.
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{
              fontFamily: "RobotoLight",
              fontWeight: 300,
              color: "#FF83A8",
              mb: 3.5,
              opacity: 0.95,
              textAlign: "center",
            }}
          >
            Software Engineer
          </Typography>
          <Typography
            variant="body1"
            color="primary"
            sx={{
              fontFamily: "RobotoLight",
              fontWeight: 300,
              mb: 2.5,
              opacity: 0.72,
              lineHeight: 1.85,
            }}
          >
            When I first joined the University of Edinburgh for my undergraduate
            studies, I thought that Computer Science was just Software
            Engineering — building products. Soon, I realised that Computer
            Science is more than that. Its capabilities are numerous and I feel
            blessed for choosing this field. The projects I have been involved
            in so far were centred around my want to improve peoples' lifestyle
            and routine.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "RobotoLight",
              fontWeight: 300,
              fontStyle: "italic",
              color: "#FF83A8",
              opacity: 0.85,
              borderLeft: "3px solid rgba(255,131,168,0.85)",
              paddingLeft: "16px",
              lineHeight: 1.7,
            }}
          >
            Creating well-designed systems by taking into consideration the
            users' needs and wants.
          </Typography>
        </GlassCard>
      </Box>

      <Box>
        <Box>
          <Timeline
            position={isMobile ? "right" : "alternate"}
            sx={
              isMobile
                ? {
                    "& .MuiTimelineItem-root::before": { flex: 0, padding: 0 },
                    "& .MuiTimelineSeparator-root": {
                      width: "40px",
                      alignItems: "center",
                    },
                  }
                : {}
            }
          >
            {fulltime.map((job, i) => (
              <TimelineItem key={i}>
                <TimelineSeparator>
                  <TimelineDot
                    variant=""
                    style={{
                      backgroundColor: "#FF83A8",
                      color: "#0e052e",
                      border: "none",
                    }}
                  >
                    {i === 0 ? <WorkIcon fontSize="small" /> : null}
                  </TimelineDot>
                  <TimelineConnector
                    style={{ backgroundColor: "rgba(255, 131, 168, 0.50)" }}
                  />
                </TimelineSeparator>
                <TimelineContent>
                  <Job
                    company={job.Company}
                    task={job.Task}
                    position={job.Position}
                    dateS={job.DateS}
                    dateE={job.DateE}
                    location={job.Location}
                    desc={job.Description}
                    index={i}
                  />
                </TimelineContent>
              </TimelineItem>
            ))}
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  variant="default"
                  style={{
                    backgroundColor: "#FF83A8",
                    color: "#0e052e",
                    border: "none",
                  }}
                >
                  <SchoolIcon fontSize="small" />
                </TimelineDot>
                <TimelineConnector
                  style={{ backgroundColor: "rgba(255,131,168,0.50)" }}
                />
              </TimelineSeparator>
              <TimelineContent>
                <EducationEnd index={fulltime.length + 1} />
              </TimelineContent>
            </TimelineItem>
            {internships.map((internship, i) => (
              <TimelineItem key={i}>
                <TimelineSeparator>
                  <TimelineDot
                    variant="default"
                    style={{
                      backgroundColor: "#FF83A8",
                      color: "#0e052e",
                      border: "none",
                    }}
                  />
                  <TimelineConnector
                    style={{ backgroundColor: "rgba(255,131,168,0.50)" }}
                  />
                </TimelineSeparator>
                <TimelineContent>
                  <Job
                    company={internship.Company}
                    task={internship.Task}
                    position={internship.Position}
                    dateS={internship.DateS}
                    dateE={internship.DateE}
                    location={internship.Location}
                    desc={internship.Description}
                    index={i}
                  />
                </TimelineContent>
              </TimelineItem>
            ))}
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  variant="default"
                  style={{
                    backgroundColor: "#FF83A8",
                    color: "#0e052e",
                    border: "none",
                  }}
                >
                  <SchoolIcon fontSize="small" />
                </TimelineDot>
              </TimelineSeparator>
              <TimelineContent>
                <EducationStart
                  index={fulltime.length + 1 + internships.length + 1}
                />
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        </Box>
      </Box>
    </Box>
  );
}
